import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { TalentSignupForm } from "@/components/TalentSignupForm";
import { TrendingUp, Wallet, ShieldCheck, Link2, BarChart3, Building2 } from "lucide-react";

const COPY = {
  talent: {
    headline: <>Ubah konten jadi <span className="text-primary">komisi</span> yang cair cepat.</>,
    features: [
      { icon: Link2, t: "Link Afiliasi per Produk", d: "Buat link khusus tiap produk & kampanye TikTok Shop" },
      { icon: BarChart3, t: "Statistik Klik & Konversi", d: "Lihat performa link Anda secara langsung" },
      { icon: Wallet, t: "Tarik Komisi Kapan Saja", d: "Cair ke rekening bank & e-wallet via Easylink" },
    ],
    title: "Masuk sebagai Talent",
    sub: "Kelola link afiliasi, pantau komisi, dan ajukan penarikan.",
    wrong: "Akun ini adalah akun admin. Silakan masuk lewat halaman login admin.",
  },
  admin: {
    headline: <>Kelola afiliasi, komisi, & <span className="text-primary">disbursement</span> agency.</>,
    features: [
      { icon: TrendingUp, t: "Tracking Penjualan Afiliasi", d: "Pantau GMV per talent & platform secara real-time" },
      { icon: Wallet, t: "Disbursement via Easylink", d: "Transfer komisi ke bank & e-wallet talent otomatis" },
      { icon: ShieldCheck, t: "Model Revenue Transparan", d: "Margin agency & bagi hasil talent yang jelas & auditable" },
    ],
    title: "Login Admin Agency",
    sub: "Khusus pengelola agency Komisia.",
    wrong: "Akun ini bukan akun admin. Talent silakan masuk lewat halaman login talent.",
  },
};

export default function Login({ portal = "talent" }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const signup = portal === "talent" && searchParams.get("daftar") === "1";
  const setSignup = (on) => setSearchParams(on ? { daftar: "1" } : {});
  const c = COPY[portal];

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, portal);
      toast.success("Login berhasil");
    } catch (err) {
      if (err.wrongPortal) { toast.error(c.wrong); return; }
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex grid-glow">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 flex-col justify-between p-12 border-r border-border relative overflow-hidden">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Komisia" className="h-11 w-11 rounded-xl shadow-lg shadow-primary/30" />
          <div>
            <p className="font-display font-extrabold text-lg leading-none">Komisia</p>
            <p className="text-xs text-muted-foreground mt-1">Affiliate & Disbursement Platform</p>
          </div>
        </div>

        <div className="space-y-8">
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold tracking-tight leading-tight">
            {c.headline}
          </h1>
          <div className="space-y-5">
            {c.features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 * i }}
                className="flex items-start gap-4"
              >
                <div className="h-10 w-10 rounded-lg bg-card border border-border grid place-items-center shrink-0">
                  <f.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">{f.t}</p>
                  <p className="text-sm text-muted-foreground">{f.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">© 2026 Komisia. All rights reserved.</p>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          className={`w-full ${signup ? "max-w-lg" : "max-w-md"} glass border border-border rounded-2xl p-8 shadow-2xl`}
        >
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <img src="/logo.png" alt="Komisia" className="h-10 w-10 rounded-xl" />
            <p className="font-display font-bold">Komisia</p>
          </div>
          {portal === "admin" && (
            <span data-testid="admin-portal-badge" className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md bg-amber-500/15 text-amber-400 border border-amber-500/30 mb-3">
              <Building2 className="h-3.5 w-3.5" /> Area Admin
            </span>
          )}
          <h2 className="font-display text-2xl font-bold" data-testid="login-title">{signup ? "Daftar Jadi Talent" : c.title}</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">{signup ? "Isi data Anda. Akun aktif setelah disetujui admin Komisia." : c.sub}</p>

          {signup ? <TalentSignupForm onBack={() => setSignup(false)} /> : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="auth-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5"
                required
              />
            </div>
            <Button
              data-testid="auth-login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-white font-semibold h-11"
            >
              {loading ? "Memproses..." : "Masuk"}
            </Button>
          </form>
          )}
          {portal === "talent" && (
            <p className="text-sm text-muted-foreground text-center mt-6">
              {signup ? "Sudah punya akun? " : "Belum punya akun? "}
              <button type="button" data-testid={signup ? "go-login-link" : "go-signup-link"} onClick={() => setSignup(!signup)} className="text-primary font-medium hover:underline">
                {signup ? "Masuk di sini" : "Daftar jadi Talent"}
              </button>
            </p>
          )}
          {portal === "admin" && (
            <p className="text-xs text-muted-foreground text-center mt-6">
              Anda talent? <Link to="/login" data-testid="go-talent-login-link" className="text-primary hover:underline">Masuk di sini</Link>
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
}
